/**
 * Browser Dispatch Payload
 *
 * What the backend hands the browser runner for one dispatch, on either lane: the hosted lane sends
 * it as the `params` of a `PROVISIONING_DISPATCH` job, and the local lane passes the same object to
 * the runner's job entry in `BROWSER_JOB_PAYLOAD`. The runner's `dispatchArgv` turns it into
 * `journey run` argv, and nothing else does (SCH-2964).
 *
 * The runner does not depend on this package (`apps/browser-runner/tests/contract-shared-types.test.ts`
 * explains why). It keeps its own lenient mirror, `provisioningDispatchParams` in
 * `apps/browser-runner/src/browser-job.ts`, and that contract test pins the two together.
 *
 * WIRE COMPATIBILITY. These are exactly the fields the deployed runner image parses. Optional fields
 * are OMITTED when unknown, never sent empty: an empty `playDeveloperId` would be read as a real
 * (and wrong) account name, and a null `runId` fails the runner's validation after the message is
 * already on the queue.
 */

import { z } from 'zod'

const id = z.string().min(1)

export const BrowserDispatchPayloadSchema = z
  .object({
    /** The provisioning run → `--run`. Absent for an app-scoped dispatch, which has no gates to close. */
    runId: id.optional(),
    /**
     * The dispatch record the runner narrates per-step progress to → `--dispatch`. Required unless
     * `dryRun` is true (refined below): a write-capable run with no record to report to is refused here.
     */
    dispatchId: id.optional(),
    /** Runner step ids. The runner expands each id's deps and runs the set in catalog order. */
    stepIds: z.array(id).min(1),
    /** `true` → `--dry-run` (report, write nothing); otherwise `--yes`. The backend always sends it. */
    dryRun: z.boolean().optional(),
    /** The registry row by public id → `--app`. How an app-scoped dispatch names its app. */
    mobileAppId: id.optional(),
    /**
     * TRANSITIONAL legacy selector → `--school`, for a runner image built before `mobileAppId`
     * existed: the school slug (Apple) or the Android package (Android). The runner prefers
     * `mobileAppId` when both are present. Removed once the deployed image is confirmed to read
     * `mobileAppId` (plan SCH-2964, Q1).
     */
    school: id.optional(),
    /** Play developer ACCOUNT id → `--play-developer`; without it the console lands on the account picker. */
    playDeveloperId: id.optional(),
    /** Play Console app id → `--play-app`; turns `play.shell`'s name search into a verification. */
    playAppId: id.optional(),
    /**
     * The version a step acts on → `--expected-version`: the version name `play.publish-changes`
     * refuses without, and the App Store version an upgrade made for `asc.reset-ratings`. Every
     * other dispatch omits it.
     */
    expectedVersion: id.optional()
  })
  .superRefine((payload, ctx) => {
    if (!payload.dispatchId && !payload.dryRun) {
      ctx.addIssue({
        code: 'custom',
        path: ['dispatchId'],
        message: 'dispatchId is required for a write-capable dispatch; pass dryRun: true for a probe-only run'
      })
    }
  })
export type BrowserDispatchPayload = z.infer<typeof BrowserDispatchPayloadSchema>
